import { z } from 'zod';
import {
  GOVERNANCE_AGREEMENT_VERSION,
  HPI_I_VALIDATION_PATTERN,
  IDENTITY_STATUSES,
  ONBOARDING_STATUSES,
  OCCUPATIONS,
  PRACTICE_MODES,
  SUBSCRIPTION_PLANS,
  TOTAL_STEPS,
} from './constants.js';

export const objectIdSchema = z.string().regex(/^[a-f0-9]{24}$/i, 'Must be a 24-character hex Mongo ObjectId');
export const guidSchema = z.string().uuid('Must be a UUID v4');

export const inviteeSchema = z.object({
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  email: z.string().email().max(254),
  contactNumber: z.string().min(6).max(40).optional(),
  occupation: z.enum(OCCUPATIONS).optional(),
  ahpraNumber: z.string().min(1).max(40).optional(),
  prescriberNumber: z.string().min(1).max(40).optional(),
  primaryPracticeName: z.string().max(200).optional(),
  plan: z.enum(SUBSCRIPTION_PLANS).optional(),
});
export type Invitee = z.infer<typeof inviteeSchema>;

export const certMetadataSchema = z.object({
  subjectCN: z.string(),
  issuerCN: z.string().optional(),
  hpiO: z.string().optional(),
  serialNumber: z.string().optional(),
  validFrom: z.string().datetime(),
  validTo: z.string().datetime(),
  verifiedAt: z.string().datetime(),
});
export type CertMetadata = z.infer<typeof certMetadataSchema>;

export const governanceAgreementRecordSchema = z.object({
  agreed: z.literal(true),
  agreedAt: z.string().datetime(),
  agreementVersion: z.string(),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
});
export type GovernanceAgreementRecord = z.infer<typeof governanceAgreementRecordSchema>;

export const hpiIRecordSchema = z.object({
  hpiI: z.string().regex(HPI_I_VALIDATION_PATTERN, {
    message:
      'Your HPI-I must be 16 digits beginning with 800361. Please double-check the value shown in PRODA.',
  }),
  recordedAt: z.string().datetime(),
});
export type HpiIRecord = z.infer<typeof hpiIRecordSchema>;

export const submitHpiISchema = z.object({
  hpiI: z
    .string()
    .trim()
    .regex(HPI_I_VALIDATION_PATTERN, {
      message:
        'Your HPI-I must be 16 digits beginning with 800361. Please double-check the value shown in PRODA.',
    }),
});

export const identityStateSchema = z.object({
  status: z.enum(IDENTITY_STATUSES),
  sumsubApplicantId: z.string().optional(),
  reviewAnswer: z.string().optional(),
  reviewResult: z.unknown().optional(),
  startedAt: z.string().datetime().optional(),
  completedAt: z.string().datetime().optional(),
});
export type IdentityState = z.infer<typeof identityStateSchema>;

export const onboardingPublicSchema = z.object({
  guid: guidSchema,
  invitee: inviteeSchema,
  practiceMode: z.enum(PRACTICE_MODES).nullable(),
  currentStep: z.number().int().min(1).max(TOTAL_STEPS),
  completedSteps: z.array(z.number().int().min(1).max(TOTAL_STEPS)),
  status: z.enum(ONBOARDING_STATUSES),
  expiresAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
  nashCert: certMetadataSchema.nullable(),
  governanceAgreement: governanceAgreementRecordSchema.nullable(),
  hpiIRecord: hpiIRecordSchema.nullable(),
  identity: identityStateSchema,
});
export type OnboardingPublic = z.infer<typeof onboardingPublicSchema>;

export const onboardingAdminSchema = onboardingPublicSchema.extend({
  _id: objectIdSchema,
  invitedBy: z.object({ _id: objectIdSchema, name: z.string(), email: z.string().email() }).nullable(),
  magicLinkUrl: z.string().url(),
  lastActivityAt: z.string().datetime(),
});
export type OnboardingAdmin = z.infer<typeof onboardingAdminSchema>;

export const updateStepSchema = z.object({
  currentStep: z.number().int().min(1).max(TOTAL_STEPS),
});

export const setPracticeModeSchema = z.object({
  mode: z.enum(PRACTICE_MODES),
});

export const acceptGovernanceSchema = z.object({
  agreementVersion: z.literal(GOVERNANCE_AGREEMENT_VERSION),
});

export const adminLoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});

export const refreshTokenSchema = z.object({
  refreshToken: z.string().min(20),
});

export const adminSchema = z.object({
  _id: objectIdSchema,
  email: z.string().email(),
  name: z.string(),
  createdAt: z.string().datetime(),
  lastLoginAt: z.string().datetime().nullable(),
});
export type Admin = z.infer<typeof adminSchema>;

export const authResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  accessTokenExpiresAt: z.string().datetime(),
  refreshTokenExpiresAt: z.string().datetime(),
  admin: adminSchema,
});
export type AuthResponse = z.infer<typeof authResponseSchema>;

export const createOnboardingSchema = z.object({
  invitee: inviteeSchema,
  ttlDays: z.number().int().min(1).max(365).optional(),
  sendEmail: z.boolean().default(true),
});
export type CreateOnboardingInput = z.infer<typeof createOnboardingSchema>;

export const listOnboardingsQuerySchema = z.object({
  status: z.enum(ONBOARDING_STATUSES).optional(),
  q: z.string().max(100).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});

export const sumsubAccessTokenResponseSchema = z.object({
  token: z.string(),
  userId: z.string(),
});
